import { useState, type FC, type MouseEvent } from "react";
import { FormProvider } from "react-hook-form";
import { axiosPost } from "@root/shared/utils/axiosForRails";
import { axiosPostCamelToSnake } from "@root/shared/utils/axiosPostCamelToSnake";
import { Grid } from "@root/shared/components/Grid";
import { Button } from "@root/shared/components/Button";
import { TextLink } from "@root/shared/components/TextLink";
import { Icon } from "@root/shared/components/Icon";
import { LazyLoadImage } from "@root/shared/components/LazyLoadImage";
import { InformationText } from "@root/shared/components/InformationText";
import { REGISTERABLE_MIN_AGE, REGISTERABLE_MAX_AGE } from "@root/shared/utils/constants";
import { useAssessmentForm } from "./useAssessmentForm";
import { type FormValues } from "./schema";
import { STEP } from "./constants";
import { AssessmentStart } from "./uiParts/AssessmentStart"; // step0
import { ProfileFields } from "./uiParts/ProfileFields"; // step1
import { JobTypeSelector } from "./uiParts/JobTypeSelector"; // step2
import { ExperienceSelector } from "./uiParts/ExperienceSelector"; // step3
import { CredentialSelector } from "./uiParts/CredentialSelector"; // step4
import { MonthlyCustomerField } from "./uiParts/MonthlyCustomerField"; // step5
import { PersonalSalesField } from "./uiParts/PersonalSalesField"; // step8
import { AvgMonthlyRequestedCustomersField } from "./uiParts/AvgMonthlyRequestedCustomersField"; // step6
import { AverageSpendingField } from "./uiParts/AverageSpendingField"; // step7
import { ContactAndPasswordFields } from "./uiParts/ContactAndPasswordFields"; // step9
import { StepIndicator } from "./uiParts/StepIndicator";
import { Loading } from "./uiParts/Loading";
import * as styles from "./styles.css";
import { Header } from "../Header";
import { Footer } from "../Footer";

const currentYear = new Date().getFullYear();

// サーバーのエラーフィールド名 → フォームのフィールド名のマッピング
const SERVER_FIELD_TO_FORM_FIELD: Record<string, keyof FormValues | null> = {
  email: "mailAddress",
  password: "password",
  sex: "sex",
  birth_day_year: "birthYear",
  birth_day_month: "birthMonth",
  birth_day_day: "birthDay",
  birth_day: "birthYear", // 生年月日の汎用エラーはbirthYearに表示
  prefecture_id: "addressPrefecture",
  city_id: "addressCity",
  job_type_id: "jobType",
  monthly_customers: "monthlyCustomers",
  avg_monthly_requested_customers: "monthlyNominatedCustomers",
  average_spending: "averageSpending",
  personal_sales: "personalSales",
  credentials: "credentials",
  experience_id: "experience",
  // 無視するフィールド
  experience_job_type_id: null, // job_type_idエラーと同時に出るため無視
  experiences: null, // フロントからの入力では発生しない
};

const yearOptions: { label: string; value: number }[] = Array.from(
  { length: REGISTERABLE_MAX_AGE - REGISTERABLE_MIN_AGE + 1 },
  (_, i) => {
    const year = currentYear - REGISTERABLE_MIN_AGE - i;
    return { label: `${year}年`, value: year };
  },
);

type Props = {
  totalCount: number;
  rejobPath: string;
  jobTypeIdOptions: { label: string; value: string }[];
  fetchPrefectureOptionsPath: string;
  fetchCityOptionsPath: string;
  emailDuplicateCheckPath: string;
  fetchCredentialOptionsPath: string;
  fetchExperienceOptionsPath: string;
  submitPath: string;
  termsPath: string;
  privacyPolicyPath: string;
  businessType: string | null;
  showCampaignBanner: boolean;
};

export const Assessment: FC<Props> = ({
  totalCount,
  rejobPath,
  jobTypeIdOptions,
  fetchPrefectureOptionsPath,
  fetchCityOptionsPath,
  emailDuplicateCheckPath,
  fetchCredentialOptionsPath,
  fetchExperienceOptionsPath,
  submitPath,
  termsPath,
  privacyPolicyPath,
  businessType,
  showCampaignBanner,
}) => {
  const [jobTypeName, setJobTypeName] = useState("");
  const [experienceName, setExperienceName] = useState("");
  const [generalErrorMessage, setGeneralErrorMessage] = useState<string | null>(null);

  const handleChangeJobType = (jobType: string) => {
    setJobTypeName(jobType);
    // 職種が変わったら資格情報をリセットする
    setValue("credentials", {}, { shouldDirty: true });
    setValue("experience", "", { shouldDirty: true });
  };

  const handleChangeExperience = (experience: string) => {
    setExperienceName(experience);
    // 経験年数が変わったら資格情報をリセットする
    setValue("credentials", {}, { shouldDirty: true });
  };
  const handleAssessmentSubmit = async (data: FormValues) => {
    try {
      const emailDuplicateResponse = await axiosPost.post<{
        status: boolean;
        msg: string | null;
      }>(emailDuplicateCheckPath, { fieldValue: data.mailAddress });

      if (!emailDuplicateResponse.data.status) {
        setError("mailAddress", { type: "manual", message: "すでに使われています" });
        return;
      }

      const sexMapping = { male: 1, female: 2 };
      const payload = {
        email: data.mailAddress,
        password: data.password,
        sex: sexMapping[data.sex as keyof typeof sexMapping],
        birthDayYear: data.birthYear,
        birthDayMonth: data.birthMonth,
        birthDayDay: data.birthDay,
        prefectureId: Number(data.addressPrefecture),
        cityId: Number(data.addressCity),
        jobTypeId: Number(data.jobType),
        monthlyCustomers: Number(data.monthlyCustomers),
        avgMonthlyRequestedCustomers: Number(data.monthlyNominatedCustomers),
        averageSpending: Number(data.averageSpending),
        personalSales: data.personalSales,
        monthlyNominatedCustomers: Number(data.monthlyNominatedCustomers),
        averagePersonalSales: data.personalSales,
        experiencesAttributes: [
          {
            jobTypeId: Number(data.jobType),
            jobTypeExperienceId: Number(data.experience),
          },
        ],
        credentialsAttributes: Object.entries(data.credentials)
          .filter(([, { willFlag }]) => typeof willFlag === "boolean")
          .map(([credentialId, { willFlag }]) => ({
            userCredentialItemId: Number(credentialId),
            willFlag,
          })),
      };

      const response = await axiosPostCamelToSnake.post<{
        errors: { field?: string; message: string }[];
        // eslint-disable-next-line camelcase
        redirect_url?: string;
      }>(submitPath, payload);

      if (response.data.redirect_url) {
        window.location.href = response.data.redirect_url;
      }
    } catch (error) {
      if (error instanceof Error && "response" in error) {
        const axiosError = error as {
          response?: { data?: { errors?: { field?: string; message: string }[] } };
        };
        const errors = axiosError.response?.data?.errors;
        if (errors && errors.length > 0) {
          const hasFieldError = errors.some((err) => err.field);
          if (hasFieldError) {
            // フィールドありのエラー: マッピングを使用してsetError
            const errorFormFields: (keyof FormValues)[] = [];
            errors.forEach((err) => {
              if (err.field) {
                const formField = SERVER_FIELD_TO_FORM_FIELD[err.field];
                // nullまたはundefinedの場合は無視
                if (!formField) return;
                setError(formField, {
                  type: "manual",
                  message: err.message,
                });
                errorFormFields.push(formField);
              }
            });
            // エラーがあるフィールドの最初のステップへ遷移
            goToStepWithErrorFields(errorFormFields);
          } else {
            // フィールドなしのエラー: サーバーからのメッセージを表示
            setGeneralErrorMessage(errors[0].message);
            goToFirstStep();
          }
        }
      }
    }
  };
  const {
    methods,
    currentStep,
    currentStepIndex,
    stepCount,
    goNext,
    goPrev,
    goToFirstStep,
    goToStepWithErrorFields,
    handleSubmit,
    isSubmitting,
    setError,
  } = useAssessmentForm(handleAssessmentSubmit);
  const { setValue } = methods;

  return (
    <>
      <Header />
      <div className={styles.container}>
        {isSubmitting ? (
          <div className={styles.loadingWrapper}>
            <Loading />
          </div>
        ) : (
          <div className={currentStep === STEP.START ? styles.stepStartWrapper : styles.wrapper}>
            {currentStep !== STEP.START && (
              <>
                <LazyLoadImage
                  src="/images/user_frontend/landing/assessment/img_eyecatch.png"
                  dummyImgPath="/images/user_frontend/landing/assessment/img_eyecatch.png"
                  alt="平均より高い？低い？売上力診断"
                  width={325}
                  height={90}
                />
                <div className={styles.stepIndicatorWrapper}>
                  <StepIndicator totalSteps={stepCount - 1} currentStep={currentStepIndex} />
                </div>
              </>
            )}
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit} noValidate>
                {currentStep === STEP.START && (
                  <AssessmentStart
                    totalCount={totalCount}
                    rejobPath={rejobPath}
                    onClickNextStep={goNext}
                    businessType={businessType}
                    showCampaignBanner={showCampaignBanner}
                  />
                )}
                {currentStep === STEP.BASIC_INFO && (
                  <>
                    {generalErrorMessage && (
                      <InformationText
                        isAlert
                        text={generalErrorMessage}
                        className={styles.errorText}
                      />
                    )}
                    <ProfileFields
                      yearOptions={yearOptions}
                      fetchPrefectureOptionsPath={fetchPrefectureOptionsPath}
                      fetchCityOptionsPath={fetchCityOptionsPath}
                    />
                  </>
                )}
                {currentStep === STEP.JOB_TYPE && (
                  <JobTypeSelector
                    jobTypeIdOptions={jobTypeIdOptions}
                    onChange={handleChangeJobType}
                  />
                )}
                {currentStep === STEP.EXPERIENCE && (
                  <ExperienceSelector
                    jobTypeIdOptions={jobTypeIdOptions}
                    experienceUrl={fetchExperienceOptionsPath}
                    onChange={handleChangeExperience}
                  />
                )}
                {currentStep === STEP.CREDENTIALS && (
                  <CredentialSelector
                    credentialUrl={fetchCredentialOptionsPath}
                    jobTypeName={jobTypeName}
                    experienceName={experienceName}
                  />
                )}
                {currentStep === STEP.MONTHLY_CUSTOMERS && <MonthlyCustomerField />}
                {currentStep === STEP.MONTHLY_NOMINATED_CUSTOMERS && (
                  <AvgMonthlyRequestedCustomersField />
                )}
                {currentStep === STEP.AVERAGE_SPENDING && <AverageSpendingField />}
                {currentStep === STEP.PERSONAL_SALES && <PersonalSalesField />}
                {currentStep === STEP.ACCOUNT && <ContactAndPasswordFields />}
                {currentStep !== STEP.START && (
                  <div className={styles.buttonsWrapper}>
                    <Buttons
                      currentStepIndex={currentStepIndex}
                      stepCount={stepCount}
                      goPrev={goPrev}
                      goNext={goNext}
                      isSubmitting={isSubmitting}
                      currentStep={currentStep}
                      termsPath={termsPath}
                      privacyPolicyPath={privacyPolicyPath}
                    />
                  </div>
                )}
              </form>
            </FormProvider>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

const GA_ACTION_MAP: Record<string, string> = {
  [STEP.BASIC_INFO]: "assess_input__basic_info",
  [STEP.JOB_TYPE]: "assess_input__occupation",
  [STEP.EXPERIENCE]: "assess_input__experience",
  [STEP.CREDENTIALS]: "assess_input__qualification",
  [STEP.MONTHLY_CUSTOMERS]: "assess_input__customer",
  [STEP.MONTHLY_NOMINATED_CUSTOMERS]: "assess_input__nomination",
  [STEP.AVERAGE_SPENDING]: "assess_input__spending",
  [STEP.PERSONAL_SALES]: "assess_input__individual_sales",
  [STEP.ACCOUNT]: "assess_input__registration",
};

const getGaActionForStep = (step: string): string => GA_ACTION_MAP[step] ?? "";

const Buttons: FC<{
  currentStepIndex: number;
  stepCount: number;
  goPrev: () => void;
  goNext: () => void;
  isSubmitting: boolean;
  currentStep: string;
  termsPath: string;
  privacyPolicyPath: string;
}> = ({
  currentStepIndex,
  stepCount,
  goPrev,
  goNext,
  isSubmitting,
  currentStep,
  termsPath,
  privacyPolicyPath,
}) => {
  const isLastStep = currentStepIndex === stepCount - 1;
  const isFirstStep = currentStepIndex === 1;
  const gaAction = getGaActionForStep(currentStep);

  const handleGoNext = (e: MouseEvent<HTMLButtonElement>) => {
    goNext();
    e.currentTarget.blur();
  };

  const handleGoPrev = (e: MouseEvent<HTMLButtonElement>) => {
    goPrev();
    e.currentTarget.blur();
  };

  if (isLastStep) {
    return (
      <Grid columns={1} gapY={16}>
        <Button
          className={styles.button}
          theme="primary"
          type="submit"
          disabled={isSubmitting}
          data-ga-action="assess_input__view_result"
        >
          会員登録して診断結果を見る
        </Button>
        <Button className={styles.button} theme="tertiary" onClick={handleGoPrev}>
          戻る
        </Button>
        <p className={styles.textLink}>
          登録すると、
          <TextLink href={termsPath} target="_blank" rel="noopener">
            ご利用規約
          </TextLink>
          ・
          <TextLink
            href={privacyPolicyPath}
            target="_blank"
            rel="noopener noreferrer"
            icon={<Icon name="target-blank" />}
          >
            個人情報保護方針
          </TextLink>
          に
          <br />
          同意したものとみなされます。
        </p>
      </Grid>
    );
  }
  if (isFirstStep) {
    return (
      <div>
        <Button
          className={styles.button}
          theme="primary"
          onClick={handleGoNext}
          data-ga-action={gaAction}
        >
          次へ進む
        </Button>
      </div>
    );
  }
  return (
    <Grid columns={2} gapX={16}>
      <Button className={styles.button} theme="tertiary" onClick={handleGoPrev}>
        戻る
      </Button>
      <Button
        className={styles.button}
        theme="primary"
        onClick={handleGoNext}
        data-ga-action={gaAction}
      >
        次へ進む
      </Button>
    </Grid>
  );
};
